import React, { useState } from 'react';
import Modal from '../../shared/modal';
import { useAppDispatch } from '../../app/hooks';
import { tryToCreateReportIssue } from '../../store/brokers/partners/documents/documentsSlice';

interface IReportIssuesModal {
    openModal: boolean;
    chatId: string;
    messageId: string;
    onSuccessReport: () => void;
    handleCloseModal: () => void;
}

const ReportIssuesModal = ({
    openModal,
    chatId,
    messageId,
    onSuccessReport,
    handleCloseModal,
}: IReportIssuesModal) => {
    const dispatch = useAppDispatch();
    const [selectedReason, setSelectedReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const onCloseModal = () => {
        handleCloseModal();
        setSelectedReason('');
        setCustomReason('');
        setSubmitting(false);
        setErrorMessage('');
    };

    const onSubmitReport = async () => {
        if (!selectedReason || (selectedReason === 'other' && !customReason.trim())) {
            setErrorMessage('Please select a reason or provide more details.');
            return;
        }

        setSubmitting(true);
        setErrorMessage('');
        try {
            const formatData = {
                topicId: chatId,
                messageId: messageId,
                userIssue: selectedReason === 'other' ? customReason : selectedReason
            }
            await dispatch(tryToCreateReportIssue(formatData)).unwrap()
            onSuccessReport();
        } catch (error) {
            setErrorMessage('Something went wrong while submitting the report. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const renderForm = () => (
        <>
            <p className='mb-5 font-bold dark:text-primary-dark'>What&apos;s the problem?</p>
            <div className='mt-5'>
                <p className='mb-2 text-sm dark:text-primary-dark'>Please select one</p>

                <div className='flex flex-col gap-3'>
                    <label className='flex items-center gap-2 cursor-pointer dark:text-primary-dark'>
                        <input
                            type='radio'
                            name='reportReason'
                            value='It misunderstood my question'
                            checked={selectedReason === 'It misunderstood my question'}
                            onChange={() => setSelectedReason('It misunderstood my question')}
                            className='accent-black dark:accent-white'
                        />
                        It misunderstood my question
                    </label>

                    <label className='flex items-center gap-2 cursor-pointer dark:text-primary-dark'>
                        <input
                            type='radio'
                            name='reportReason'
                            value='The answer is incorrect'
                            checked={selectedReason === 'The answer is incorrect'}
                            onChange={() => setSelectedReason('The answer is incorrect')}
                            className='accent-black dark:accent-white'
                        />
                        The answer is incorrect
                    </label>

                    <label className='flex items-center gap-2 cursor-pointer dark:text-primary-dark'>
                        <input
                            type='radio'
                            name='reportReason'
                            value=' It gave a confusing response'
                            checked={selectedReason === ' It gave a confusing response'}
                            onChange={() => setSelectedReason(' It gave a confusing response')}
                            className='accent-black dark:accent-white'
                        />
                        It gave a confusing response
                    </label>

                    <label className='flex items-start gap-2 cursor-pointer dark:text-primary-dark'>
                        <input
                            type='radio'
                            name='reportReason'
                            value='other'
                            checked={selectedReason === 'other'}
                            onChange={() => setSelectedReason('other')}
                            className='accent-black dark:accent-white mt-1'
                        />
                        <div className='w-full'>
                            Other
                            {selectedReason === 'other' && (
                                <textarea
                                    value={customReason}
                                    onChange={(e) => setCustomReason(e.target.value)}
                                    placeholder='Please describe the issue...'
                                    className='mt-2 w-full p-2 border rounded-md dark:bg-bgColor-darkMode dark:text-primary-dark dark:border-gray-600'
                                    rows={3}
                                />
                            )}
                        </div>
                    </label>

                    {errorMessage && (
                        <p className='text-sm text-red-500'>{errorMessage}</p>
                    )}
                </div>

                <div className='mt-10 flex justify-end gap-3'>
                    <button
                        onClick={onCloseModal}
                        className='btn-primary !bg-gray-500 hover:opacity-80 dark:bg-gray-700 dark:text-primary-dark'
                    >
                        Close
                    </button>
                    <button
                        onClick={onSubmitReport}
                        disabled={submitting}
                        className=' btn-primary  text-white hover:opacity-80 disabled:opacity-50 dark:bg-black'
                    >
                        {submitting ? 'Submitting...' : 'Report Issue'}
                    </button>
                </div>
            </div>
        </>
    );

    return (
        <Modal
            open={openModal}
            onClose={onCloseModal}
            contentContainerStyle='!rounded-xl min-w-[95%] md:min-w-[70%] lg:min-w-[50%]'
        >
            <div className='p-2 min-w-[95%] md:min-w-[70%] lg:min-w-[45%]'>
                {renderForm()}
            </div>
        </Modal>
    );
};

export default ReportIssuesModal;
